/**
 * BM-H3724 (5 Oct 2026). A conflict check saved at "pending approval" whose
 * request never reached a director: no approval link, no director named.
 * Nobody could approve it and no screen offered a way out (BL.RS.006.1537,
 * stuck since 22 Sep). The usual cause was the old CDD check on conflict
 * approval, which the strict golden rule chain could never satisfy; Vanessa
 * moved it off on 5 Oct. This sends the request now.
 */
import { useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { approvalRecipient } from "../utils/openConflictCheck";
import { Button } from "./ui/button";

export function neverSentForApproval(cc) {
  return !!cc && cc.conflict_status === "pending_approval" && !approvalRecipient(cc);
}

export default function ConflictSendForApproval({ cc, onSent }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");

  if (!neverSentForApproval(cc) && !sentTo) return null;

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/conflict-approval/submit`, {
        conflict_check_id: cc.id,
        decision_notes: cc.decision_notes || "",
      });
      const name = res.data?.recipient_name || res.data?.recipient_email || "a director";
      setSentTo(name);
      onSent && onSent(res.data);
    } catch (err) {
      setError(errorMessage(err, "Could not send this for approval"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-amber-950/30 border border-amber-700/50 rounded-lg p-4 space-y-2" data-testid="conflict-send-for-approval">
      {sentTo ? (
        <p className="text-sm text-amber-100" data-testid="conflict-send-for-approval-sent">
          Sent to {sentTo} for approval. They have been emailed the approval link.
        </p>
      ) : (
        <>
          <p className="text-sm text-amber-100 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            This check is waiting for approval, but it was never sent to a director, so nobody can approve it yet.
          </p>
          <Button onClick={send} disabled={busy} data-testid="conflict-send-for-approval-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send for approval
          </Button>
          {error && <p className="text-xs text-red-300" data-testid="conflict-send-for-approval-error">{error}</p>}
        </>
      )}
    </div>
  );
}
